/**
 * SLA / overdue rules for workflow tasks (Phase 1).
 * ACER: anchor + 28 days is the regulatory due date for lodgement tracking.
 */

import { TASK_STATUS } from '../../workflow/taskModel';

/**
 * @param {string|null} dueAt ISO date string (date part)
 * @param {string} [todayIso] — for tests
 * @returns {boolean}
 */
export function isPastDue(dueAt, todayIso) {
  if (!dueAt) return false;
  const today = todayIso ? new Date(todayIso) : new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueAt);
  due.setHours(0, 0, 0, 0);
  return due.getTime() < today.getTime();
}

/**
 * Recompute status: pending/in_progress → overdue if past due and not completed.
 * @param {import('../../workflow/taskModel').WorkflowTask} task
 * @param {string} [todayIso]
 * @returns {import('../../workflow/taskModel').WorkflowTask}
 */
export function applyOverdueStatus(task, todayIso) {
  if (task.status === TASK_STATUS.COMPLETED || task.status === TASK_STATUS.CANCELLED) {
    return task;
  }
  if (task.status === TASK_STATUS.BLOCKED) return task;
  if (!task.dueAt) return task;
  if (!isPastDue(task.dueAt, todayIso)) return task;
  if (task.status === TASK_STATUS.OVERDUE) return task;
  return {
    ...task,
    status: TASK_STATUS.OVERDUE,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * ACER regulatory reminder windows (days remaining until anchor+28).
 * @param {string} admissionDateIso — YYYY-MM-DD
 * @param {string} [todayIso]
 * @returns {{ daysUntilAcerDue: number, alertLevel: 'none'|'info'|'warning'|'critical' }}
 */
export function acerSlaFromAdmission(admissionDateIso, todayIso) {
  const today = todayIso ? new Date(todayIso) : new Date();
  today.setHours(0, 0, 0, 0);
  const adm = new Date(admissionDateIso);
  adm.setHours(0, 0, 0, 0);
  const acerDue = new Date(adm);
  acerDue.setDate(acerDue.getDate() + 28);
  const ms = acerDue.getTime() - today.getTime();
  const daysUntilAcerDue = Math.ceil(ms / (24 * 60 * 60 * 1000));

  let alertLevel = 'none';
  if (daysUntilAcerDue <= 0) alertLevel = 'critical';
  else if (daysUntilAcerDue <= 3) alertLevel = 'critical';
  else if (daysUntilAcerDue <= 7) alertLevel = 'warning';
  else if (daysUntilAcerDue <= 14) alertLevel = 'info';

  return { daysUntilAcerDue, alertLevel };
}
